import { computed, onUnmounted, ref } from "vue";
import { useStore } from "vuex";
import {
  allegroConfigMessagesConst,
  allegroConfigPermissionsConst,
} from "../constants/allegroConstants";
import { hasPermission } from "../permissions";

const AUTH_INFO_POLL_INTERVAL = 5000;

export const getAllegroErrorMessage = (data, fallback) => {
  if (Array.isArray(data?.nonFieldErrors)) {
    return data.nonFieldErrors[0];
  }
  if (Array.isArray(data?.non_field_errors)) {
    return data.non_field_errors[0];
  }
  const firstError = Object.entries(data || {})[0];
  if (!firstError) {
    return fallback || "Nie udało się połączyć konta z Allegro.";
  }
  return Array.isArray(firstError[1])
    ? firstError[1][0]
    : firstError[0] + ": " + firstError[1];
};

/**
 * Allegro OAuth device flow shared by the account list and the account
 * overview - both screens drive the connection exactly the same way in the old
 * admin panel (account-list.controller.js / account-overview.controller.js).
 *
 * @param {Object} options
 * @param {Function} options.resolveAccount - returns the live account object
 *   for a configuration pk; the account is looked up on every poll because the
 *   underlying data may get reloaded and a captured reference would be stale.
 * @param {Function} [options.onChange] - called after the connection state
 *   changes, so the caller can refresh its data.
 */
export function useAllegroConnection({ resolveAccount, onChange }) {
  const store = useStore();

  // Mirrors accountIntervals from the old admin panel: one polling interval
  // per configuration, checking the authorization status.
  const accountIntervals = {};

  const showToaster = (type, content) => {
    store.dispatch("showToaster", { type: type, content: content });
  };

  const notifyChange = () => {
    if (typeof onChange === "function") {
      onChange();
    }
  };

  const stopPolling = (configurationPk) => {
    if (accountIntervals[configurationPk]) {
      clearInterval(accountIntervals[configurationPk]);
      delete accountIntervals[configurationPk];
    }
  };

  const getAuthInfo = (configurationPk) => {
    const account = resolveAccount(configurationPk);
    if (!account) {
      stopPolling(configurationPk);
      return;
    }

    store
      .dispatch("getAllegroOauthAuthInfo", configurationPk)
      .then((response) => {
        const deviceFlow = response.data?.device_flow || null;
        const accessToken = response.data?.access_token || null;
        const revokedToken = response.data?.revoked_token || null;

        account.configuration.device_flow = deviceFlow;
        account.configuration.access_token = accessToken;

        if (!deviceFlow && !accessToken && revokedToken) {
          const reason = revokedToken.revoke_reason_code;
          if (reason === "email_verification_failed") {
            showToaster(
              "error",
              allegroConfigMessagesConst.revokedTokenInvalidEmailError
            );
          } else if (reason === "account_id_verification_failed") {
            showToaster(
              "error",
              allegroConfigMessagesConst.revokedTokenInvalidAccountIdError
            );
          }

          account.loadingAccessToken = false;
          stopPolling(configurationPk);
          return;
        }

        if (accountIntervals[configurationPk] && accessToken !== null) {
          account.loadingAccessToken = false;
          stopPolling(configurationPk);
          showToaster(
            "success",
            allegroConfigMessagesConst.accessTokenRetriveSuccess
          );
          notifyChange();
        }
      })
      .catch(() => {
        account.loadingAccessToken = false;
        stopPolling(configurationPk);
      });
  };

  // Starts polling for an account left mid-authorization. Safe to call on
  // every data reload - it returns early when there is nothing to wait for.
  const refreshAuthInfo = (account) => {
    const configuration = account?.configuration;
    if (!configuration || !configuration.device_flow) {
      return;
    }
    if (configuration.access_token) {
      return;
    }

    account.loadingAccessToken = true;
    if (!accountIntervals[configuration.pk]) {
      accountIntervals[configuration.pk] = setInterval(
        () => getAuthInfo(configuration.pk),
        AUTH_INFO_POLL_INTERVAL
      );
    }
  };

  // Connecting the account rewrites the configuration, disconnecting removes
  // its token - the same permissions the buttons are shown for.
  const canAssociateAccount = computed(() =>
    hasPermission(allegroConfigPermissionsConst.update)
  );
  const canRevokeAccountToken = computed(() =>
    hasPermission(allegroConfigPermissionsConst.delete)
  );

  const getDeviceFlow = (account) => {
    if (!canAssociateAccount.value) {
      return;
    }
    if (!account?.configuration?.pk) {
      showToaster("error", allegroConfigMessagesConst.noConfigurationError);
      return;
    }

    account.loadingDeviceCode = true;

    store
      .dispatch("getAllegroOauthDeviceFlow", account.configuration.pk)
      .then((response) => {
        account.configuration.device_flow = response.data;
        refreshAuthInfo(account);
        showToaster(
          "success",
          allegroConfigMessagesConst.createDeviceFlowSuccess
        );
      })
      .catch((error) => {
        if (error?.response?.status === 400) {
          showToaster("error", getAllegroErrorMessage(error.response.data));
        } else {
          showToaster("error", allegroConfigMessagesConst.error);
        }
      })
      .finally(() => {
        account.loadingDeviceCode = false;
      });
  };

  const accountToRevoke = ref(null);
  const revokeModalContent = computed(() =>
    accountToRevoke.value
      ? `Czy na pewno chcesz rozłączyć konto: <b>${accountToRevoke.value.name}</b> ?`
      : ""
  );

  const openRevokeModal = (account) => {
    if (!canRevokeAccountToken.value) {
      return;
    }
    if (!account?.configuration?.access_token) {
      showToaster("error", allegroConfigMessagesConst.accessTokenRevokeError);
      return;
    }
    accountToRevoke.value = account;
  };

  const closeRevokeModal = () => {
    accountToRevoke.value = null;
  };

  const revokeAccessToken = () => {
    if (!canRevokeAccountToken.value) {
      return;
    }

    const account = accountToRevoke.value;
    accountToRevoke.value = null;
    account.loadingRevoke = true;

    store
      .dispatch("revokeAllegroOauthToken", account.configuration.pk)
      .then((response) => {
        if (response.data?.is_revoked) {
          account.configuration.access_token = null;
          showToaster(
            "success",
            allegroConfigMessagesConst.accessTokenRevokeSuccess
          );
          notifyChange();
        }
      })
      .catch((error) => {
        showToaster(
          "error",
          getAllegroErrorMessage(
            error?.response?.data,
            allegroConfigMessagesConst.error
          )
        );
      })
      .finally(() => {
        account.loadingRevoke = false;
      });
  };

  onUnmounted(() => {
    Object.keys(accountIntervals).forEach(stopPolling);
  });

  return {
    accountToRevoke,
    revokeModalContent,
    canAssociateAccount,
    canRevokeAccountToken,
    refreshAuthInfo,
    getDeviceFlow,
    openRevokeModal,
    closeRevokeModal,
    revokeAccessToken,
  };
}
